// Chat del copiloto Jido — historial local + textarea + envío.
//
// Sin streaming (F2 MVP): la respuesta llega completa vía POST /api/jido/chat
// (ver useCopiloto). El historial vive en `useState` — no se persiste entre
// aperturas del drawer ni entre pantallas.
//
// Rediseño editorial ("confianza en el número"): cuando la respuesta trae
// `datos` (JidoDato[]), se pinta la "anatomía de dato" ENCIMA de la prosa
// markdown. Sin `datos` → solo prosa (comportamiento previo).

import { useState, type FormEvent, type KeyboardEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  JIDO_HISTORY_MAX_CHARS,
  JIDO_HISTORY_MAX_TURNOS,
  type JidoChatTurno,
  type JidoDato,
} from 'schemas';
import { useCopiloto } from './useCopiloto';
import { DatoBloque } from './DatoBloque';
import { ChipsSugerencia } from './ChipsSugerencia';
import { EstadoPensando } from './EstadoPensando';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { ApiError } from '../../lib/api/client';
import { cn } from '../../lib/utils';

interface ChatMessage extends JidoChatTurno {
  id: string;
  /** Solo en mensajes del assistant: bloques de anatomía de dato (opcional). */
  datos?: JidoDato[];
}

/** Espejo de `jidoChatRequestSchema.history` (packages/schemas/src/jido.ts). */
const MAX_HISTORY_TURNOS = JIDO_HISTORY_MAX_TURNOS;

const MSG_ERROR_GENERICO = 'No pudimos enviar su mensaje. Intente de nuevo.';

let contador = 0;
function nextId(): string {
  contador += 1;
  return `jido-msg-${contador}`;
}

export function CopilotoChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [texto, setTexto] = useState('');
  const copiloto = useCopiloto();

  function enviar(mensajeArg?: string) {
    const mensaje = (mensajeArg ?? texto).trim();
    if (!mensaje || copiloto.isPending) return;

    // Historial ANTES de agregar el mensaje actual — el backend lo espera
    // como turnos previos, el mensaje nuevo va en `message`.
    // Recortamos cada turno al máximo del contrato: una respuesta más larga
    // hacía fallar el `parse` client-side sin siquiera enviar (bug real en
    // prod: "No pudimos enviar su mensaje" al hacer follow-up de una respuesta
    // larga). Recortar preserva el contexto y nunca rompe el envío.
    const historia = messages.slice(-MAX_HISTORY_TURNOS).map(({ role, content }) => ({
      role,
      content: content.slice(0, JIDO_HISTORY_MAX_CHARS),
    }));

    setMessages((prev) => [...prev, { id: nextId(), role: 'user', content: mensaje }]);
    setTexto('');

    copiloto.mutate(
      { message: mensaje, history: historia },
      {
        onSuccess: (data) => {
          setMessages((prev) => [
            ...prev,
            { id: nextId(), role: 'assistant', content: data.reply, datos: data.datos },
          ]);
        },
      },
    );
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    enviar();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  }

  const errorMessage = copiloto.isError
    ? copiloto.error instanceof ApiError
      ? copiloto.error.message
      : MSG_ERROR_GENERICO
    : null;

  const vacio = messages.length === 0;

  return (
    <div className="flex h-full flex-col">
      <div
        role="log"
        aria-label="Historial de conversación con Jido"
        className="flex-1 space-y-4 overflow-y-auto"
      >
        {vacio ? (
          <EstadoVacio onSugerencia={(t) => enviar(t)} disabled={copiloto.isPending} />
        ) : null}

        {messages.map((m) =>
          m.role === 'user' ? (
            <div
              key={m.id}
              className="ml-auto max-w-[85%] rounded-2xl rounded-br-sm bg-accent px-3 py-2 text-sm text-accent-ink"
            >
              {m.content}
            </div>
          ) : (
            <div key={m.id} className="mr-auto max-w-[92%] space-y-3">
              {/* Anatomía de dato ENCIMA de la prosa */}
              {m.datos && m.datos.length > 0
                ? m.datos.map((d, i) => <DatoBloque key={`${m.id}-dato-${i}`} dato={d} />)
                : null}

              <div
                className={cn(
                  'text-sm text-fg',
                  '[&_p]:mb-2 [&_p:last-child]:mb-0',
                  '[&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4',
                  '[&_strong]:font-semibold [&_code]:rounded [&_code]:bg-surface-overlay [&_code]:px-1 [&_code]:font-mono',
                )}
              >
                <ReactMarkdown>{m.content}</ReactMarkdown>
              </div>
            </div>
          ),
        )}

        {copiloto.isPending ? <EstadoPensando /> : null}

        {errorMessage ? (
          <div
            role="alert"
            className="mr-auto max-w-[85%] rounded-lg bg-crit-soft px-3 py-2 text-sm text-crit"
          >
            {errorMessage}
          </div>
        ) : null}
      </div>

      <div className="mt-3 flex-shrink-0 space-y-2 border-t border-border pt-3">
        {/* Chips-sugerencia sobre el input (ocultos en el estado vacío, que ya
            los muestra en grande). */}
        {!vacio ? (
          <ChipsSugerencia onSelect={(t) => enviar(t)} disabled={copiloto.isPending} />
        ) : null}

        <form onSubmit={handleSubmit} className="flex items-end gap-2">
          <Textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Escriba su pregunta…"
            rows={2}
            aria-label="Mensaje para Jido"
            className="flex-1 resize-none"
          />
          <Button type="submit" disabled={!texto.trim()} loading={copiloto.isPending}>
            Enviar
          </Button>
        </form>
      </div>
    </div>
  );
}

/** Estado vacío — saludo editorial + qué sabe hacer Jido + chips de arranque. */
function EstadoVacio({
  onSugerencia,
  disabled,
}: {
  onSugerencia: (texto: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-4 pt-2">
      <h3 className="font-serif text-3xl leading-tight text-fg">Hola. Soy Jido.</h3>
      <p className="text-sm leading-relaxed text-fg-secondary">
        Le ayudo a leer su operación: producción, cumplimiento, paros, turnos, personal y, si tiene
        permiso, dinero. Pregunte en lenguaje natural; cada número que le doy sale de la base de
        datos, con su período y su fuente.
      </p>
      <p className="text-sm text-fg-tertiary">Pruebe con:</p>
      <ChipsSugerencia onSelect={onSugerencia} disabled={disabled} />
    </div>
  );
}
